/**
 * @fileoverview An order's three statuses (`orders."status"`, `"paymentStatus"`, `"fulfillmentStatus"`) — their
 * vocabularies, the lifecycle's machine, the payment classes, the fulfilment sets and the badge tones, declared once
 * (REGISTER-WEB-ORDERS-001 B1, which builds ORD1: owner rulings Q-ORD1-1..4 and Q-D2-1, MUTATION-INSTRUMENT-001).
 *
 * ## Why it lives here
 *
 * The `orders` table is shared: the ParcelPuffin register (pos-api) and the PurchasePuffin storefront, partner API,
 * staff admin and quote conversion (purchase-api) all write it, and pos-web, purchase-web and the console read it.
 * Four vocabularies judged its three columns and disagreed: pos-api's `constants/statuses.js` (a lifecycle with
 * `confirmed`, `shipped` and `delivered`, payments `unpaid` / `partial`), purchase-api's `constants/orderStatuses.js`
 * (a lifecycle with `completed`, `on_hold` and `refunded`), unified-models' `OrderModel.ORDER_STATUSES` (a third list
 * nothing wrote with) and the web apps' hand copies. Owner ruling Q-ORD1-1: "Yes, one screen + one status list". Every
 * writer, validator, judge and screen reads these.
 *
 * ## The lifecycle (`status`)
 *
 * The machine is Q-ORD1-2's, verbatim: pending → processing or on_hold; processing → on_hold or completed; on_hold →
 * pending or processing; completed and cancelled are final. Auto-complete (paid AND fulfilled) is the order-actions
 * engine's, which moves `processing → completed`.
 *
 * - A cancel reaches `cancelled` from every status that is not final. Q-ORD1-2 names no cancel edge, but it makes
 *   only `completed` and `cancelled` final, and Q-ORD1-3 ("Cancel offers full refund") presumes an order can be
 *   cancelled — holding an order must not make it uncancellable.
 * - `refunded` is NOT a lifecycle status. Q-D2-1: "Only payment status changes" — a refund moves `paymentStatus`.
 * - `shipped` / `delivered` are fulfilment, and `paid` is payment: the retired lists mixed the axes.
 * - pos-api's `confirmed` (every register sale) maps by the auto-complete rule: a register sale paid and handed over at
 *   the counter is `completed`.
 *
 * ## The payment axis (`paymentStatus`)
 *
 * `paymentStatus` is the ONE record of whether the business holds the buyer's money, so every status belongs to
 * exactly one class, and `orderPaymentStatusClass` answers `null` — UNKNOWN, fail closed — for anything else.
 * `partially_paid` is an order with some of its money taken and a balance owed (pay at pickup in several tenders);
 * pos-api's dead `partial` is retired into it, named to read beside `partially_refunded`. pos-api's `unpaid` is
 * `pending`. `failed` awaits payment: after a failed charge the PaymentIntent can still be paid.
 *
 * ## The fulfilment axis (`fulfillmentStatus`)
 *
 * A pickup order is marked `ready_for_pickup` (which e-mails the buyer) and is `fulfilled` at its hand-over; a shipped
 * order is `shipped`, then `delivered`. `returned` is gone: nothing ever wrote it — a return is recorded by its lines
 * and the refund's payment status.
 *
 * @module @puffin-core/status-taxonomy/orderStatus
 */

'use strict';

const { BADGE_TONE } = require('./badgeTones');

/** The empty answer, shared so a caller can never mutate a status's targets through it. */
const NO_TARGETS = Object.freeze([]);

// ── The lifecycle ────────────────────────────────────────────────────────────────────────────────────────────────

/**
 * An order's lifecycle.
 *
 * @type {Readonly<Object<string, string>>}
 */
const ORDER_STATUS = Object.freeze({
  PENDING: 'pending',
  PROCESSING: 'processing',
  ON_HOLD: 'on_hold',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled',
});

/**
 * Every lifecycle status, in declaration order — what a validator admits, a filter offers and the CHECK names.
 *
 * @type {ReadonlyArray<string>}
 */
const ORDER_STATUS_VALUES = Object.freeze(Object.values(ORDER_STATUS));

/**
 * Every legal lifecycle transition, spelled once. Every status is a KEY; a final one maps to `[]`.
 *
 * @type {Readonly<Object<string, ReadonlyArray<string>>>}
 */
const ORDER_STATUS_TRANSITIONS = Object.freeze({
  [ORDER_STATUS.PENDING]: Object.freeze([
    ORDER_STATUS.PROCESSING,
    ORDER_STATUS.ON_HOLD,
    ORDER_STATUS.CANCELLED,
  ]),
  [ORDER_STATUS.PROCESSING]: Object.freeze([
    ORDER_STATUS.ON_HOLD,
    ORDER_STATUS.COMPLETED,
    ORDER_STATUS.CANCELLED,
  ]),
  [ORDER_STATUS.ON_HOLD]: Object.freeze([
    ORDER_STATUS.PENDING,
    ORDER_STATUS.PROCESSING,
    ORDER_STATUS.CANCELLED,
  ]),
  [ORDER_STATUS.COMPLETED]: NO_TARGETS,
  [ORDER_STATUS.CANCELLED]: NO_TARGETS,
});

/**
 * The statuses an order at `status` may move to next — what a screen offers and the engine allows.
 *
 * Reads OWN keys only, so an unknown value (or an inherited `Object.prototype` name such as `constructor`) answers
 * `[]` rather than a function.
 *
 * @param {*} status - The order's current lifecycle status.
 * @returns {ReadonlyArray<string>} The legal targets, in the machine's order; `[]` when final or unknown.
 */
function orderStatusTargets(status) {
  return typeof status === 'string' &&
    Object.prototype.hasOwnProperty.call(ORDER_STATUS_TRANSITIONS, status)
    ? ORDER_STATUS_TRANSITIONS[status]
    : NO_TARGETS;
}

/**
 * The statuses of an order still being worked — every status with somewhere to go. Derived from the machine.
 *
 * @type {ReadonlyArray<string>}
 */
const ORDER_OPEN_STATUSES = Object.freeze(
  ORDER_STATUS_VALUES.filter((status) => ORDER_STATUS_TRANSITIONS[status].length > 0),
);

/**
 * The statuses after which nothing moves — the order is finished or cancelled. Derived from the machine.
 *
 * @type {ReadonlyArray<string>}
 */
const ORDER_FINAL_STATUSES = Object.freeze(
  ORDER_STATUS_VALUES.filter((status) => ORDER_STATUS_TRANSITIONS[status].length === 0),
);

/**
 * The statuses from which the machine lets an order be cancelled. Derived from the machine.
 *
 * @type {ReadonlyArray<string>}
 */
const ORDER_CANCELLABLE_STATUSES = Object.freeze(
  ORDER_STATUS_VALUES.filter((status) =>
    ORDER_STATUS_TRANSITIONS[status].includes(ORDER_STATUS.CANCELLED),
  ),
);

/**
 * Each lifecycle status's badge tone (`<StatusBadgeCell statusTones>`, Q-MI-25): waiting `warning`, being worked
 * `info`, the happy end `success`, the abandoned end `danger`.
 *
 * @type {Readonly<Object<string, string>>}
 */
const ORDER_STATUS_BADGE_TONES = Object.freeze({
  [ORDER_STATUS.PENDING]: BADGE_TONE.WARNING,
  [ORDER_STATUS.PROCESSING]: BADGE_TONE.INFO,
  [ORDER_STATUS.ON_HOLD]: BADGE_TONE.WARNING,
  [ORDER_STATUS.COMPLETED]: BADGE_TONE.SUCCESS,
  [ORDER_STATUS.CANCELLED]: BADGE_TONE.DANGER,
});

// ── The payment axis ─────────────────────────────────────────────────────────────────────────────────────────────

/**
 * An order's payment status.
 *
 * @type {Readonly<Object<string, string>>}
 */
const ORDER_PAYMENT_STATUS = Object.freeze({
  PENDING: 'pending',
  AUTHORIZED: 'authorized',
  PARTIALLY_PAID: 'partially_paid',
  PAID: 'paid',
  PARTIALLY_REFUNDED: 'partially_refunded',
  REFUNDED: 'refunded',
  FAILED: 'failed',
});

/** @type {ReadonlyArray<string>} */
const ORDER_PAYMENT_STATUS_VALUES = Object.freeze(Object.values(ORDER_PAYMENT_STATUS));

/**
 * The payment statuses that RETURN money: a staff change to one is a refund (PERMISSIONS-CANON-001 ruling Q-B8-2).
 *
 * @type {ReadonlyArray<string>}
 */
const ORDER_REFUND_PAYMENT_STATUSES = Object.freeze([
  ORDER_PAYMENT_STATUS.PARTIALLY_REFUNDED,
  ORDER_PAYMENT_STATUS.REFUNDED,
]);

/**
 * The three classes a payment status belongs to: nothing taken yet, money (or a hold on the card) held, and
 * everything taken given back.
 *
 * @type {Readonly<Object<string, string>>}
 */
const ORDER_PAYMENT_CLASS = Object.freeze({
  AWAITING_PAYMENT: 'awaitingPayment',
  MONEY_HELD: 'moneyHeld',
  RETURNED: 'returned',
});

/**
 * Every payment status → its class, keyed by the vocabulary so a status added without a class is a red, never a
 * silent default.
 *
 * @type {Readonly<Object<string, string>>}
 */
const ORDER_PAYMENT_STATUS_CLASSES = Object.freeze({
  [ORDER_PAYMENT_STATUS.PENDING]: ORDER_PAYMENT_CLASS.AWAITING_PAYMENT,
  [ORDER_PAYMENT_STATUS.AUTHORIZED]: ORDER_PAYMENT_CLASS.MONEY_HELD,
  [ORDER_PAYMENT_STATUS.PARTIALLY_PAID]: ORDER_PAYMENT_CLASS.MONEY_HELD,
  [ORDER_PAYMENT_STATUS.PAID]: ORDER_PAYMENT_CLASS.MONEY_HELD,
  [ORDER_PAYMENT_STATUS.PARTIALLY_REFUNDED]: ORDER_PAYMENT_CLASS.MONEY_HELD,
  [ORDER_PAYMENT_STATUS.REFUNDED]: ORDER_PAYMENT_CLASS.RETURNED,
  [ORDER_PAYMENT_STATUS.FAILED]: ORDER_PAYMENT_CLASS.AWAITING_PAYMENT,
});

/**
 * The class of a payment status, or `null` for a value outside the vocabulary. A caller that decides about money
 * treats `null` as UNKNOWN and fails closed.
 *
 * @param {*} paymentStatus - An `orders."paymentStatus"` value.
 * @returns {string|null} An `ORDER_PAYMENT_CLASS` value, or `null`.
 */
function orderPaymentStatusClass(paymentStatus) {
  return typeof paymentStatus === 'string' &&
    Object.prototype.hasOwnProperty.call(ORDER_PAYMENT_STATUS_CLASSES, paymentStatus)
    ? ORDER_PAYMENT_STATUS_CLASSES[paymentStatus]
    : null;
}

/**
 * The payment statuses that still wait on the buyer's payment (a failed payment may be tried again): the awaiting-payment
 * class, derived so a status added to that class is waited on. What a settle may still mark paid
 * (`@puffin-core/order-engine` `settlePaid`), and what checkout keeps resumable.
 *
 * @type {ReadonlyArray<string>}
 */
const ORDER_AWAITING_PAYMENT_STATUSES = Object.freeze(
  ORDER_PAYMENT_STATUS_VALUES.filter(
    (status) => ORDER_PAYMENT_STATUS_CLASSES[status] === ORDER_PAYMENT_CLASS.AWAITING_PAYMENT,
  ),
);

/**
 * The payment statuses whose money is WHOLLY in hand: paid, and paid with part of it since returned (REGISTER-WEB-ORDERS-001
 * B2; ruling 30, "hand-over refuses while a balance is owed"). A part of the money-held class: `partially_paid` still
 * owes a balance, and `authorized` is a hold on a card that was never captured.
 *
 * @type {ReadonlyArray<string>}
 */
const ORDER_PAYMENT_FULLY_SETTLED_STATUSES = Object.freeze([
  ORDER_PAYMENT_STATUS.PAID,
  ORDER_PAYMENT_STATUS.PARTIALLY_REFUNDED,
]);

/**
 * Whether an order's money is wholly in hand. Anything else, a value outside the vocabulary included, is `false`, so a
 * decision that needs the money (a pickup order's hand-over) fails closed.
 *
 * @param {*} paymentStatus - An `orders."paymentStatus"` value.
 * @returns {boolean}
 */
function isOrderPaymentFullySettled(paymentStatus) {
  return ORDER_PAYMENT_FULLY_SETTLED_STATUSES.includes(paymentStatus);
}

/**
 * Each payment status's badge tone: owed `warning`, a card hold `info`, paid `success`, a failed charge `danger`, all
 * money returned `neutral`.
 *
 * @type {Readonly<Object<string, string>>}
 */
const ORDER_PAYMENT_STATUS_BADGE_TONES = Object.freeze({
  [ORDER_PAYMENT_STATUS.PENDING]: BADGE_TONE.WARNING,
  [ORDER_PAYMENT_STATUS.AUTHORIZED]: BADGE_TONE.INFO,
  [ORDER_PAYMENT_STATUS.PARTIALLY_PAID]: BADGE_TONE.WARNING,
  [ORDER_PAYMENT_STATUS.PAID]: BADGE_TONE.SUCCESS,
  [ORDER_PAYMENT_STATUS.PARTIALLY_REFUNDED]: BADGE_TONE.WARNING,
  [ORDER_PAYMENT_STATUS.REFUNDED]: BADGE_TONE.NEUTRAL,
  [ORDER_PAYMENT_STATUS.FAILED]: BADGE_TONE.DANGER,
});

// ── The fulfilment axis ──────────────────────────────────────────────────────────────────────────────────────────

/**
 * An order's fulfilment status.
 *
 * @type {Readonly<Object<string, string>>}
 */
const ORDER_FULFILLMENT_STATUS = Object.freeze({
  UNFULFILLED: 'unfulfilled',
  PROCESSING: 'processing',
  PARTIAL: 'partial',
  READY_FOR_PICKUP: 'ready_for_pickup',
  SHIPPED: 'shipped',
  DELIVERED: 'delivered',
  FULFILLED: 'fulfilled',
});

/** @type {ReadonlyArray<string>} */
const ORDER_FULFILLMENT_STATUS_VALUES = Object.freeze(Object.values(ORDER_FULFILLMENT_STATUS));

/** The fulfilment status of an order nothing has been made or moved for yet. */
const ORDER_UNSTARTED_FULFILLMENT_STATUS = ORDER_FULFILLMENT_STATUS.UNFULFILLED;

/**
 * A pickup order's two steps: staff mark it READY, and it is fulfilled at its HAND-OVER.
 *
 * @type {Readonly<{READY: string, HANDED_OVER: string}>}
 */
const ORDER_PICKUP_FULFILLMENT = Object.freeze({
  READY: ORDER_FULFILLMENT_STATUS.READY_FOR_PICKUP,
  HANDED_OVER: ORDER_FULFILLMENT_STATUS.FULFILLED,
});

/**
 * The fulfilment statuses a pickup order may be marked ready from: nothing has left the store, and it is not ready.
 *
 * @type {ReadonlyArray<string>}
 */
const ORDER_PICKUP_READY_FROM_FULFILLMENT_STATUSES = Object.freeze([
  ORDER_FULFILLMENT_STATUS.UNFULFILLED,
  ORDER_FULFILLMENT_STATUS.PROCESSING,
  ORDER_FULFILLMENT_STATUS.PARTIAL,
]);

/**
 * The fulfilment statuses a carrier gives an order. At these the goods are with the carrier, so a cancel is refused:
 * `ORDER_UNCANCELLABLE_FULFILLMENT_STATUSES` is this same list.
 *
 * @type {ReadonlyArray<string>}
 */
const ORDER_CARRIER_FULFILLMENT_STATUSES = Object.freeze([
  ORDER_FULFILLMENT_STATUS.SHIPPED,
  ORDER_FULFILLMENT_STATUS.DELIVERED,
]);

/**
 * The fulfilment statuses at which a cancel is refused — the carrier statuses (a handed-over pickup order is
 * `completed`, which is final).
 *
 * @type {ReadonlyArray<string>}
 */
const ORDER_UNCANCELLABLE_FULFILLMENT_STATUSES = ORDER_CARRIER_FULFILLMENT_STATUSES;

/**
 * The fulfilment statuses at which the goods have left the store — with a carrier, or handed over. A refund's
 * "return to stock" is off for these until the goods come back (Q-PAY1-2).
 *
 * @type {ReadonlyArray<string>}
 */
const ORDER_GOODS_LEFT_FULFILLMENT_STATUSES = Object.freeze([
  ...ORDER_CARRIER_FULFILLMENT_STATUSES,
  ORDER_FULFILLMENT_STATUS.FULFILLED,
]);

/**
 * Each fulfilment status's badge tone: not started `neutral`, in motion `info`, part done `warning`, done `success`.
 *
 * @type {Readonly<Object<string, string>>}
 */
const ORDER_FULFILLMENT_STATUS_BADGE_TONES = Object.freeze({
  [ORDER_FULFILLMENT_STATUS.UNFULFILLED]: BADGE_TONE.NEUTRAL,
  [ORDER_FULFILLMENT_STATUS.PROCESSING]: BADGE_TONE.INFO,
  [ORDER_FULFILLMENT_STATUS.PARTIAL]: BADGE_TONE.WARNING,
  [ORDER_FULFILLMENT_STATUS.READY_FOR_PICKUP]: BADGE_TONE.INFO,
  [ORDER_FULFILLMENT_STATUS.SHIPPED]: BADGE_TONE.INFO,
  [ORDER_FULFILLMENT_STATUS.DELIVERED]: BADGE_TONE.SUCCESS,
  [ORDER_FULFILLMENT_STATUS.FULFILLED]: BADGE_TONE.SUCCESS,
});

module.exports = {
  ORDER_STATUS,
  ORDER_STATUS_VALUES,
  ORDER_STATUS_TRANSITIONS,
  ORDER_OPEN_STATUSES,
  ORDER_FINAL_STATUSES,
  ORDER_CANCELLABLE_STATUSES,
  ORDER_STATUS_BADGE_TONES,
  orderStatusTargets,
  ORDER_PAYMENT_STATUS,
  ORDER_PAYMENT_STATUS_VALUES,
  ORDER_PAYMENT_CLASS,
  ORDER_PAYMENT_STATUS_CLASSES,
  ORDER_REFUND_PAYMENT_STATUSES,
  ORDER_PAYMENT_FULLY_SETTLED_STATUSES,
  ORDER_AWAITING_PAYMENT_STATUSES,
  ORDER_PAYMENT_STATUS_BADGE_TONES,
  orderPaymentStatusClass,
  isOrderPaymentFullySettled,
  ORDER_FULFILLMENT_STATUS,
  ORDER_FULFILLMENT_STATUS_VALUES,
  ORDER_UNSTARTED_FULFILLMENT_STATUS,
  ORDER_PICKUP_FULFILLMENT,
  ORDER_PICKUP_READY_FROM_FULFILLMENT_STATUSES,
  ORDER_CARRIER_FULFILLMENT_STATUSES,
  ORDER_UNCANCELLABLE_FULFILLMENT_STATUSES,
  ORDER_GOODS_LEFT_FULFILLMENT_STATUSES,
  ORDER_FULFILLMENT_STATUS_BADGE_TONES,
};
